import React from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { reducerPaths } from "../../../../../app/reducerPaths";
import { useAddNegotiationMutation, useLazyGetNegotiationTimeoutDetailsQuery } from "../../../../../toolkit/carsInNegotiation/carsInNegotiationApi";
import { useCountdownTimer } from "../../../../../customHooks/useCountdownTimer";
import { useFormik } from "formik";
import { setAlertDialog } from "../../../../../toolkit/alertDialog/alertDialogSlice";
import theme from "../../../../../theme";
import { ArrowBack, Close, Done } from "@mui/icons-material";
import TimerWidget from "../../TimerWidget";
import { getCommaFormattedNumber } from "../../../../../utils/methods/stringMethods";
import { negotiationStatuses } from "../../../../../utils/constants/negotiationStatuses";
import PriceInput from "../../../../../components/custom/CustomInputs/PriceInput";
import CustomCheckbox from "../../../../../components/ui/CustomCheckbox";
import { auctionTypes, localAuction } from "../../../../../utils/constants/auction";
import { useTranslation } from "react-i18next";
import { convertUtcStringFormat } from "../../../../../utils/methods/dateTime";
import { routes } from "../../../../../utils/constants/routes";
import { useLocation, useNavigate } from "react-router-dom";
import { USER_ROLES } from "../../../../../utils/constants/userRoles";

const CarBiddingNegotiation = ({
  selectedCar,
  Vehicle_Id,
  dealerId,
  Bid_Id,
  negotiationHistory = [],
  firstBid,
  isLoading = false,
  negotiationStatus,
  onPrevSectionHandler,
  scrollToBottom,
  setScrollToBottom
}) => {

  const { t } = useTranslation("carBiddingNegotiation");

  // static data start...
  const suggestions = [
    t("Too Low"),
    t("I need more"),
    t("Let's have a deal"),
    t("Firm"),
    t("I won't accept less than my current Price"),
    t("Make another offer"),
    t("Increase Offer by $500"),
  ];

  const initialValues = React.useMemo(() => {
    return {
      BidID: "",
      VehicleID: "",
      CounterPrice: "",
      Comments: "",
      Action: ""
    }
  }, []);
  // static data end...

  const location = useLocation();
  const navigate = useNavigate();

  const liveAuctionOrBuyNow = location?.pathname?.includes?.(`${routes.carsInNegotiationBuyNow}`)
    ? auctionTypes.buyNow
    : auctionTypes.liveAuction;

  // useDispatch...
  const dispatch = useDispatch();
  // useDispatch...

  // useSelector start...
  const auth = useSelector(store => store[reducerPaths.auth]);
  // useSelector end...

  const [selectedSuggestion, setSelectedSuggestion] = React.useState(null);
  const [isNegotiationAcceptLoading, setIsNegotiationAcceptLoading] = React.useState({
    negotiationId: null,
    isLoading: false
  });
  const [isNegotiationDeclineLoading, setIsNegotiationDeclineLoading] = React.useState({
    negotiationId: null,
    isLoading: false
  });

  const [addNegotiationApiHandler, addNegotiationApiData] = useAddNegotiationMutation();
  const [getNegotiationTimeoutDetailsApiHandler, getNegotiationTimeoutDetailsApiData] = useLazyGetNegotiationTimeoutDetailsQuery();
  const { countDown, isInitialised } = useCountdownTimer({
    seconds: getNegotiationTimeoutDetailsApiData?.data?.counterseconds || 0
  });

  // form schema start...
  const negotiationSchema = Yup.object().shape({
    BidID: Yup.string()
      .required(t("This field is required!")),
    VehicleID: Yup.string()
      .required(t("This field is required!")),
    CounterPrice: Yup.number()
      .required(),
    Comments: Yup.string(),
    Action: Yup.string()
      .oneOf(["Accept", "Decline", ""])
  });

  const negotiationFormik = useFormik({
    initialValues,
    validationSchema: negotiationSchema,
    onSubmit: async (values) => {

      const dealerLatestOffer = getDealerLatestOffer() || firstBid?.Bid_Amount;

      if (dealerLatestOffer && values?.CounterPrice < dealerLatestOffer) {
        dispatch(
          setAlertDialog({
            open: true,
            title: t("Error!"),
            message: `${`Please enter an offer greater than the ${selectedCar?.auction_type === localAuction ? "dealer's" : "buyer's"} latest offer. Latest offer is: $${getCommaFormattedNumber(dealerLatestOffer)}`}`
          })
        );

        return;
      }

      const payload = {
        ...values,
      };

      const formData = new FormData();

      for (let i = 0; i < Object.entries(payload)?.length; i++) {
        formData.append(
          Object.entries(payload)?.[i]?.[0],
          Object.entries(payload)?.[i]?.[1]
        );
      }

      const res = await addNegotiationApiHandler({
        payload: formData
      });

      if (res?.error || ("status" in res?.data && !res?.data?.status)) {
        dispatch(
          setAlertDialog({
            open: true,
            title: t("Error!"),
            message: res?.error?.data?.error || res?.error?.data?.message || res?.data?.message || t("Some error occured!")
          })
        );
      } else {
        negotiationFormik.setValues(values => {
          return {
            ...values,
            CounterPrice: "",
            Comments: ""
          }
        });
      }
    }
  })
  // form schema end...

  // Handlers start...
  const acceptOfferHandler = async (offer) => {

    const payload = {
      ...(negotiationFormik?.values || {}),
    };

    const formData = new FormData();

    for (let i = 0; i < Object.entries(payload)?.length; i++) {
      formData.append(
        Object.entries(payload)?.[i]?.[0],
        Object.entries(payload)?.[i]?.[0] === "BidID" ?
          Bid_Id :
        Object.entries(payload)?.[i]?.[0] === "VehicleID" ?
          Vehicle_Id :
        Object.entries(payload)?.[i]?.[0] === "CounterPrice" ? 
          offer?.conter_offer :
        Object.entries(payload)?.[i]?.[0] === "Action" ?
          "Accept" : 
        Object.entries(payload)?.[i]?.[1]
      );
    };

    setIsNegotiationAcceptLoading({
      negotiationId: offer?.id,
      isLoading: true
    });

    const res = await addNegotiationApiHandler({
      payload: formData
    }); 

    if (res?.error || ("status" in res?.data && !res?.data?.status)) {
      dispatch(
        setAlertDialog({
          open: true,
          title: t("Error!"),
          message: res?.error?.data?.message || res?.error?.data?.error ||
            res?.data?.message || t("Some error occured!")
        })
      )
    }

    setIsNegotiationAcceptLoading({
      negotiationId: offer?.id,
      isLoading: false
    });
  };

  const declineOfferHandler = async (offer) => {

    const payload = {
      ...(negotiationFormik?.values || {}),
    };

    const formData = new FormData();

    for (let i = 0; i < Object.entries(payload)?.length; i++) {
      formData.append(
        Object.entries(payload)?.[i]?.[0],
        Object.entries(payload)?.[i]?.[0] === "BidID" ?
          Bid_Id :
        Object.entries(payload)?.[i]?.[0] === "VehicleID" ?
          Vehicle_Id :
        Object.entries(payload)?.[i]?.[0] === "CounterPrice" ? 
          offer?.conter_offer :
        Object.entries(payload)?.[i]?.[0] === "Action" ?
          "Decline" : 
        Object.entries(payload)?.[i]?.[1]
      );
    };

    setIsNegotiationDeclineLoading({
      negotiationId: offer?.id,
      isLoading: true
    });

    const res = await addNegotiationApiHandler({
      payload: formData
    }); 

    if (res?.error || ("status" in res?.data && !res?.data?.status)) {
      dispatch(
        setAlertDialog({
          open: true,
          title: t("Error!"),
          message: res?.error?.data?.message || res?.error?.data?.error ||
            res?.data?.message || t("Some error occured!")
        })
      )
    }

    setIsNegotiationDeclineLoading({
      negotiationId: offer?.id,
      isLoading: false
    });
  };

  const getYourHighestOffer = () => {
    const yourHighestOffer = negotiationHistory
      ?.filter?.(offer => {
        return (offer?.sender_id === auth?.user_id);
      })
      ?.map(offer => {
        return {
          ...offer,
          conter_offer: parseInt(offer?.conter_offer)
        }
      })
      ?.sort?.((a, b) => {
        if ( a?.conter_offer < b?.conter_offer ){
          return 1;
        }
        if ( a?.conter_offer > b?.conter_offer ){
          return -1;
        }
      })?.[0]?.conter_offer;

    return yourHighestOffer;
  };

  const getYourLowestOffer = () => {
    const yourHighestOffer = negotiationHistory
      ?.filter?.(offer => {
        return (offer?.sender_id === auth?.user_id);
      })
      ?.map(offer => {
        return {
          ...offer,
          conter_offer: parseInt(offer?.conter_offer)
        }
      })
      ?.sort?.((a, b) => {
        if ( a?.conter_offer < b?.conter_offer ){
          return -1;
        }
        if ( a?.conter_offer > b?.conter_offer ){
          return 1;
        }
      })?.[0]?.conter_offer;

    return yourHighestOffer;
  };

  const getDealerLatestOffer = () => {
    const dealerLatestOffer = [...(negotiationHistory
      ?.map?.(offer => {
        return {
          ...offer,
          created_at: convertUtcStringFormat(offer?.created_at),
        }
      })) || []]
      ?.sort?.((a, b) => new Date(b?.created_at) - new Date(a?.created_at))
      ?.find(offer => offer?.sender_id === offer?.dealer_id);

    return dealerLatestOffer?.conter_offer ? dealerLatestOffer?.conter_offer : null;
  }

  const getDealerHighestOffer = () => {
    let dealerHighestOffer;

    dealerHighestOffer = negotiationHistory
      ?.filter?.(offer => {
        return (offer?.sender_id === offer?.dealer_id);
      })
      ?.map(offer => {
        return {
          ...offer,
          conter_offer: parseInt(offer?.conter_offer)
        }
      })
      ?.sort?.((a, b) => {
        if ( a?.conter_offer < b?.conter_offer ){
          return 1;
        }
        if ( a?.conter_offer > b?.conter_offer ){
          return -1;
        }
      })?.[0]?.conter_offer;

    if (!dealerHighestOffer) {
      dealerHighestOffer = firstBid?.Bid_Amount;
    }

    return dealerHighestOffer;
  };

  const getNegotiationSuggestions = () => {

    const reservePrice = parseInt(selectedCar?.Reserve_Price);

    const sellerLastOffer = getYourHighestOffer() || parseInt(selectedCar?.Reserve_Price);
    const dealerLastOffer = getDealerHighestOffer() || parseInt(firstBid);

    if (!sellerLastOffer || !dealerLastOffer) {
      return null;
    }

    // Define jump slabs based on reserve price
    const getSteps = (price) => {
      if (price < 5000) return [100, 200, 300];
      if (price < 10000) return [200, 300, 500];
      if (price < 20000) return [200, 300, 500];
      if (price < 50000) return [500, 1000, 1500];
      return [1000, 1500, 2000];
    };
  
    const [step1, step2, step3] = getSteps(reservePrice);
  
    const sellerStart = sellerLastOffer ?? reservePrice;
    const dealerStart = dealerLastOffer;
  
    const sellerSuggestions = [
      sellerStart - step1,
      sellerStart - step2,
      sellerStart - step3
    ];
  
    const dealerSuggestions = [
      dealerStart + step1,
      dealerStart + step2,
      dealerStart + step3
    ];
  
    return {
      offer1: Math.floor(sellerSuggestions[0]),
      offer2: Math.floor(sellerSuggestions[1]),
      offer3: Math.floor(sellerSuggestions[2]),
    };
  };

  const handleOfferButtonClick = (offerAmount) => {
    const dealerHighestOffer = negotiationHistory
      ?.filter?.(offer => {
        return (offer?.sender_id === offer?.dealer_id);
      })
      ?.map(offer => {
        return {
          ...offer,
          conter_offer: parseInt(offer?.conter_offer)
        }
      })
      ?.sort?.((a, b) => {
        if ( a?.conter_offer < b?.conter_offer ){
          return 1;
        }
        if ( a?.conter_offer > b?.conter_offer ){
          return -1;
        }
      })?.[0]?.conter_offer;

    const yourHighestOffer = negotiationHistory
      ?.filter?.(offer => {
        return (offer?.sender_id === auth?.user_id);
      })
      ?.map(offer => {
        return {
          ...offer,
          conter_offer: parseInt(offer?.conter_offer)
        }
      })
      ?.sort?.((a, b) => {
        if ( a?.conter_offer < b?.conter_offer ){
          return 1;
        }
        if ( a?.conter_offer > b?.conter_offer ){
          return -1;
        }
      })?.[0]?.conter_offer;

    let offer1, offer2, offer3;

    if ((!yourHighestOffer && !dealerHighestOffer) || (yourHighestOffer && dealerHighestOffer && dealerHighestOffer > yourHighestOffer)) {
      return null;
    }

    if (!yourHighestOffer) {
      offer1 = dealerHighestOffer + 500;
      offer2 = dealerHighestOffer + 800;
      offer3 = dealerHighestOffer + 1000;
    } else if (!dealerHighestOffer) {
      return null;
    } else {
      const difference = yourHighestOffer - dealerHighestOffer;

      // Calculate offers based on 20%, 25%, and 30% of the difference
      offer1 = yourHighestOffer - (difference * 0.20);
      offer2 = yourHighestOffer - (difference * 0.25); // Mid-point for better options
      offer3 = yourHighestOffer - (difference * 0.30);

      // Ensure offers don't exceed the seller's highest offer and are rounded
      offer1 = Math.round(Math.min(offer1, yourHighestOffer));
      offer2 = Math.round(Math.min(offer2, yourHighestOffer));
      offer3 = Math.round(Math.min(offer3, yourHighestOffer));

      // Optional: Ensure offers are always increasing or decreasing as expected
      // For instance, if you want offer1 < offer2 < offer3
      const sortedOffers = [offer1, offer2, offer3].sort((a, b) => a - b);
      offer1 = sortedOffers[0];
      offer2 = sortedOffers[1];
      offer3 = sortedOffers[2];
    }

    return {
      offer1,
      offer2,
      offer3
    }
  };
  // Handlers end...

  // useEffect start...
  React.useEffect(() => {
    if (Vehicle_Id && Bid_Id) {
      negotiationFormik.setValues(values => {
        return {
          ...values,
          VehicleID: Vehicle_Id,
          BidID: Bid_Id
        }
      });
    }
  }, [Vehicle_Id, Bid_Id]);

  React.useEffect(() => {
    if (Vehicle_Id && Bid_Id) {
      (async () => {
        await getNegotiationTimeoutDetailsApiHandler({
          Vehicle_Id,
          Bid_Id
        })
      })();
    }
  }, [Vehicle_Id]);

  React.useEffect(() => {
    if (scrollToBottom) {
      const chatContainer = document.querySelector('.chat-container');
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
        setScrollToBottom(false);
      }
    }
  }, [scrollToBottom]);

  React.useEffect(() => {

    if (countDown?.hours === 0 && countDown?.minutes === 0 && countDown?.seconds === 0 && isInitialised && negotiationStatus === negotiationStatuses.timeout) {
      dispatch(
        setAlertDialog({
          open: true,
          title: `Alert!`,
          message: `The negotiation time is over. would you like to go to the negotiation page?`,
          onOk: () => {
            navigate(`${routes.dashboardNavigation}${
              liveAuctionOrBuyNow === auctionTypes.liveAuction ? routes.carsInNegotiation : routes.carsInNegotiationBuyNow}`);
          }
        })
      );
    }

    if (negotiationStatus === negotiationStatuses.accepted) {
      dispatch(
        setAlertDialog({
          open: true,
          title: `Success!`,
          message: `The negotiation has been accepted and deal is closed. Would you like to go to Your Orders page?`,
          onOk: () => {
            navigate(`${routes.dashboardNavigation}${auth?.user_role === USER_ROLES.admin ? routes.allOrders : routes.myOrders}`)
          }
        })
      );
    } else if (negotiationStatus === negotiationStatuses.decline) {
      dispatch(
        setAlertDialog({
          open: true,
          title: `Alert!`,
          message: `The negotiation has been declined and no deal was made. Would you like to go to the Negotiation page?`,
          onOk: () => {
            navigate(`${routes.dashboardNavigation}${
              liveAuctionOrBuyNow === auctionTypes.liveAuction ? routes.carsInNegotiation : routes.carsInNegotiationBuyNow}`);
          }
        })
      );
    }
  }, [
    negotiationStatus,
  ]);
  // useEffect end...

  console.log(negotiationHistory, Vehicle_Id,
  Bid_Id, negotiationFormik, getNegotiationSuggestions(), selectedCar, `negotiationHistory`)

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: "0.75rem",
          alignItems: "center"
        }}
      
      >
        <Button
          sx={{
            alignSelf:  "flex-start",
            marginTop: "0.25rem",
            padding: "0 !important",
            minWidth: "0 !important"
          }}
          onClick={() => {
            if (onPrevSectionHandler) {
              onPrevSectionHandler();
            }
          }}
        >
          <ArrowBack
            sx={{
              fontSize: "1.5rem"
            }}
          />
        </Button>
        <Box>
          <Typography
            variant="h6"
            align="left"
            fontWeight="bold"
            color={theme.palette.primary.main}
            mb={2}
            sx={{
              marginBottom: "0 !important",
              fontSize: "1rem"
            }}
          >
            {t("CAR BIDDING NEGOTIATION")}
          </Typography>

          {dealerId ? <Typography
            variant="subtitle1"
            align="left"
            fontWeight="bold"
            color={theme.palette.text.primary}
            sx={{
              fontSize: "1rem",
              marginBottom: "0.5rem"
            }}
          >
            {selectedCar?.auction_type === localAuction ? t("Dealer ID") : t("Buyer ID")}: {dealerId}
          </Typography> : null}
        </Box>
      </Box>

      <Box
        sx={{
          marginTop: "1rem"
        }}
      >
        {!getNegotiationTimeoutDetailsApiData?.isFetching ? <TimerWidget
          hours={countDown?.hours || 0}
          mins={countDown?.minutes || 0}
          seconds={countDown?.seconds || 0}
        /> : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center"
            }}
          >
            <CircularProgress 
              size={22}
            />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: "column",
          marginTop: "1rem",
          gap: "1rem",
          height: "25rem",
          // maxHeight: "30rem",
          overflow: "auto"
        }}
        className={`chat-container`}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignmentBaseline: "center"
            }}
          >
            <CircularProgress size={22} />
          </Box>
        ) : (
          <>
            <Box 
              sx={{ 
                // width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                backgroundColor: theme.palette.background.paper, 
                padding: "1rem", borderRadius: "0.5rem", 
                boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.1)",
                alignSelf: "center"
              }}
            >
              <Box>
                <Typography variant="h6" 
                  align={"center"}
                  sx={{
                    color: `${theme.palette.text.primary} !important`
                  }}
                >
                  ${getCommaFormattedNumber(firstBid?.Bid_Amount)}
                </Typography>

                <Box
                    sx={{
                    display: "flex",
                    flexDirection: "column"
                  }}
                >

                  <Typography
                    fontSize="0.75rem" align="right" variant="subtitle2"
                  >
                    {selectedCar?.auction_type === localAuction ? t("Dealer ID") : t("Buyer ID")} ({firstBid?.Dealer_Id || t("N/A")})
                  </Typography>
                </Box>
              </Box>

              {auth?.user_role === USER_ROLES.seller && getDealerHighestOffer() === firstBid?.Bid_Amount ? <Box
                sx={{
                  display: "flex",
                  gap: "0.5rem"
                }}
              >
                <Button
                  sx={{
                    padding: "1rem",
                    minWidth: "0 !important",
                    background: "#2BD24C"
                  }}
                  onClick={() => {
                    dispatch(
                      setAlertDialog({
                        open: true,
                        title: t("Alert!"),
                        message: t("Are you sure you want to accept this offer? The negotiation will be accepted and closed!"),
                        onOk: () => {
                          acceptOfferHandler({
                            ...firstBid,
                            conter_offer: firstBid?.Bid_Amount
                          });
                        }
                      })
                    )
                  }}
                  disabled={isNegotiationAcceptLoading?.isLoading}
                >
                  {isNegotiationAcceptLoading?.isLoading && isNegotiationAcceptLoading?.negotiationId === firstBid?.id ? (
                    <CircularProgress size={22} />
                  ) : (
                    <Done 
                      sx={{
                        color: theme.palette.background.paper
                      }}
                    />
                  )}
                </Button>

                <Button
                  sx={{
                    padding: "1rem",
                    minWidth: "0 !important",
                    background: "#D22B2B"
                  }}
                  onClick={() => {
                    dispatch(
                      setAlertDialog({
                        open: true,
                        title: t("Alert!"),
                        message: t("Are you sure you want to decline this offer? The negotiation will be declined and closed!"),
                        onOk: () => {
                          declineOfferHandler({
                            ...firstBid,
                            conter_offer: firstBid?.Bid_Amount
                          });
                        }
                      })
                    )
                  }}
                  disabled={isNegotiationDeclineLoading?.isLoading}
                >
                  {isNegotiationDeclineLoading?.isLoading && isNegotiationDeclineLoading?.negotiationId === firstBid?.id ? (
                    <CircularProgress size={22} />
                  ) : (
                    <Close 
                      sx={{
                        color: theme.palette.background.paper
                      }}
                    />
                  )}
                </Button>
              </Box> : null}
            </Box>
            {negotiationHistory?.map?.(negotiation => {
              return (
                <Box
                  key={negotiation?.id || ""} 
                  sx={{ 
                    display: "flex", 
                    justifyContent: USER_ROLES.seller === negotiation?.sender_role ? "flex-end" : "flex-start",
                    alignItems: "center", 
                    gap: "0.5rem",
                  }}
                >
                  <Box 
                    sx={{ 
                      backgroundColor: 
                        USER_ROLES.seller === negotiation?.sender_role ? theme.palette.text.primary : theme.palette.primary.main, 
                      padding: "1rem",
                      paddingRight: USER_ROLES.seller === negotiation?.sender_role ? "1rem" : "3rem",
                      paddingLeft: USER_ROLES.seller === negotiation?.sender_role ? "3rem" : "1rem",
                      borderRadius: "0.5rem", maxWidth: "12rem", 
                      boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.1)" 
                    }}
                  >
                    <Typography variant="h6" 
                      align={USER_ROLES.seller === negotiation?.sender_role ? "right" : "left"}
                      sx={{
                        color: `${USER_ROLES.seller === negotiation?.sender_role ? theme.palette.text.secondary : theme.palette.text.secondary} !important`
                      }}
                    >
                      ${getCommaFormattedNumber(negotiation?.conter_offer)}
                    </Typography>
                    {/* <Typography fontSize="0.85rem" color="gray" align="right" variant="subtitle2">Best Offer</Typography> */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column"
                      }}
                    >
                      {negotiation?.message ? <Typography 
                        fontSize="0.75rem" 
                        align={negotiation?.sender_role === USER_ROLES.seller ? "right" : "left"} 
                        variant="subtitle2"
                        sx={{
                          color: `${USER_ROLES.seller === negotiation?.sender_role ? theme.palette.text.secondary : theme.palette.text.secondary} !important`
                        }}
                      >
                        {negotiation?.message || ""}
                      </Typography> : null}

                      {negotiation?.sender_role === USER_ROLES.seller ? <Typography
                        fontSize="0.75rem" align="right" variant="subtitle2"
                        sx={{
                          color: `${USER_ROLES.seller === negotiation?.sender_role ? theme.palette.text.secondary : theme.palette.text.secondary} !important`
                        }}
                      >
                        {t("Seller ID:")} {negotiation?.sender_id || t("N/A")}
                      </Typography> : null}

                      {negotiation?.sender_role !== USER_ROLES.seller ? <Typography
                        fontSize="0.75rem" align="right" variant="subtitle2"
                        sx={{
                          color: `${USER_ROLES.seller === negotiation?.sender_role ? theme.palette.text.secondary : theme.palette.text.secondary} !important`
                        }}
                      >
                        {selectedCar?.auction_type === localAuction ? t("Dealer ID") : t("Buyer ID")}: {negotiation?.dealer_id || t("N/A")}
                      </Typography> : null}
                    </Box>
                  </Box>

                  {auth?.user_role === USER_ROLES.seller && negotiation?.sender_id !== auth?.user_id
                  && getDealerHighestOffer() === parseInt(negotiation?.conter_offer) ? <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem"
                    }}
                  >
                    <Button
                      sx={{
                        padding: "1rem",
                        minWidth: "0 !important",
                        background: "#2BD24C"
                      }}
                      onClick={() => {
                        dispatch(
                          setAlertDialog({
                            open: true,
                            title: t("Alert!"),
                            message: t("Are you sure you want to accept this offer? The negotiation will be accepted and closed!"),
                            onOk: () => {
                              acceptOfferHandler(negotiation);
                            }
                          })
                        )
                      }}
                      disabled={isNegotiationAcceptLoading?.isLoading}
                    >
                      {isNegotiationAcceptLoading?.isLoading && isNegotiationAcceptLoading?.negotiationId === negotiation?.id ? (
                        <CircularProgress size={22} />
                      ) : (
                        <Done
                          sx={{
                            color: theme.palette.background.paper
                          }}
                        />
                      )}
                    </Button>

                    <Button
                      sx={{
                        padding: "1rem",
                        minWidth: "0 !important",
                        background: "#D22B2B"
                      }}
                      onClick={() => {
                        dispatch(
                          setAlertDialog({
                            open: true,
                            title: t("Alert!"),
                            message: t("Are you sure you want to decline this offer? The negotiation will be declined and closed!"),
                            onOk: () => {
                              declineOfferHandler(negotiation);
                            }
                          })
                        )
                      }}
                      disabled={isNegotiationDeclineLoading?.isLoading}
                    >
                      {isNegotiationDeclineLoading?.isLoading && isNegotiationDeclineLoading?.negotiationId === negotiation?.id ? (
                        <CircularProgress size={22} />
                      ) : (
                        <Close
                          sx={{
                            color: theme.palette.background.paper
                          }}
                        />
                      )}
                    </Button>
                  </Box> : null}
                </Box>
              )
            })}
          </>
        )}
      </Box>
      
      {auth?.user_role === USER_ROLES.seller && getNegotiationTimeoutDetailsApiData?.isFetching ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignmentBaseline: "center"
          }}
        >
          <CircularProgress size={22} />
        </Box>
      ) : auth?.user_role === USER_ROLES.seller && (countDown?.hours > 0 || countDown?.minutes > 0 || countDown?.seconds > 0) && negotiationStatus === negotiationStatuses?.ongoing ? <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography fontSize="0.9rem" fontWeight="bold" marginTop="1rem"
          color={theme.palette.text.primary}
        >
          {t("Counter Amount(Start negotiation)")}
        </Typography>

        {getNegotiationSuggestions() ? (
          <Box sx={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem", flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              sx={{
                borderColor: theme.palette.border.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.text.secondary,
                },
                borderRadius: "0.5rem",
                backgroundColor: "transparent"
              }}
              onClick={() => {
                negotiationFormik.setValues(values => {
                  return {
                    ...values,
                    CounterPrice: getNegotiationSuggestions().offer1
                  }
                })
              }}
            >
              {t("Offer")} ${getCommaFormattedNumber(getNegotiationSuggestions().offer1)}
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderColor: theme.palette.border.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.text.secondary,
                },
                borderRadius: "0.5rem",
                backgroundColor: "transparent"
              }}
              onClick={() => {
                negotiationFormik.setValues(values => {
                  return {
                    ...values,
                    CounterPrice: getNegotiationSuggestions().offer2
                  }
                })
              }}
            >
              {t("Offer")} ${getCommaFormattedNumber(getNegotiationSuggestions().offer2)}
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderColor: theme.palette.border.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.text.secondary,
                },
                borderRadius: "0.5rem",
                backgroundColor: "transparent"
              }}
              onClick={() => {
                negotiationFormik.setValues(values => {
                  return {
                    ...values,
                    CounterPrice: getNegotiationSuggestions().offer3
                  }
                })
              }}
            >
              {t("Offer")} ${getCommaFormattedNumber(getNegotiationSuggestions().offer3)}
            </Button>
          </Box>
        ) : null} 

        <PriceInput
          name={"CounterPrice"}
          fullWidth 
          variant="outlined" 
          placeholder={t("$ Type Amount Here")} 
          sx={{ marginTop: "0.5rem" }}
          value={negotiationFormik?.values?.CounterPrice}
          onChange={negotiationFormik?.handleChange}
        />
        
        <Typography fontSize="0.9rem" fontWeight="bold" marginTop="1rem"
          color={theme.palette.text.primary}
        >
          {t("Suggestions")}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginTop: "0.5rem" }}>
          {suggestions.map((label, index) => (
            <CustomCheckbox
              key={index}
              label={label}
              selected={negotiationFormik.values?.Comments === label}
              onClick={() => {
                negotiationFormik.setValues(values => {
                  return {
                    ...values,
                    Comments: label
                  }
                });
              }}
            />
          ))}
        </Box>

        <Button 
          fullWidth variant="contained" 
          sx={{ backgroundColor: theme.palette.primary.main, marginTop: "1rem", padding: "0.75rem" }}
          onClick={negotiationFormik.handleSubmit}
          disabled={addNegotiationApiData?.isLoading}
        >
          {addNegotiationApiData?.isLoading ? (
            <CircularProgress 
              size={22}
              sx={{
                color: theme.palette.background.paper
              }}
            />
          ) : (
            t("Submit")
          )}
        </Button>
      </Box> : null}
    </>
  );
};

export default CarBiddingNegotiation;